// Service worker registration and the update flow (HARDEN-pwa). Production builds only, and not
// against the in-memory fake API (the e2e and visual suites run a production build of it) unless
// `VITE_PWA=1` asks for it. A new build found by the browser waits; the page offers a small
// "Update available" toast whose Reload tells the waiting worker to take over, and the page
// reloads once it controls the page. An hourly check finds builds deployed while a tab stays open.

/** Whether to register: a production build, against the real API (or forced by the flag). */
export function shouldRegister(env: {
  prod: boolean;
  fakeApi: boolean;
  flag?: string | undefined;
}): boolean {
  if (!env.prod) return false;
  if (env.flag === "0") return false;
  return !env.fakeApi || env.flag === "1";
}

export type UpdateToast = (o: { onReload: () => void }) => void;

const HOUR = 3_600_000;

export async function registerServiceWorker(o: {
  showUpdate: UpdateToast;
  nav?: Pick<Navigator, "serviceWorker">;
  reload?: () => void;
}): Promise<ServiceWorkerRegistration | null> {
  const nav = o.nav ?? (typeof navigator === "undefined" ? undefined : navigator);
  const sw = nav?.serviceWorker;
  if (!sw) return null;
  const reload = o.reload ?? (() => window.location.reload());
  let reg: ServiceWorkerRegistration;
  try {
    reg = await sw.register("/sw.js", { scope: "/" });
  } catch {
    return null; // a private window or a blocked worker: the app works without it
  }
  // Reload only when the person asked for the update (the first install claims the page too).
  let asked = false;
  let reloading = false;
  const offer = (worker: ServiceWorker) =>
    o.showUpdate({
      onReload: () => {
        asked = true;
        worker.postMessage({ type: "SKIP_WAITING" });
      },
    });
  // Only an update replaces something: the first install has no controller yet.
  if (reg.waiting && sw.controller) offer(reg.waiting);
  reg.addEventListener("updatefound", () => {
    const next = reg.installing;
    next?.addEventListener("statechange", () => {
      if (next.state === "installed" && sw.controller) offer(next);
    });
  });
  sw.addEventListener("controllerchange", () => {
    if (reloading || !asked) return;
    reloading = true;
    reload();
  });
  setInterval(() => void reg.update().catch(() => {}), HOUR);
  return reg;
}
